import { add, qs, qsa, remove } from '@fluorescent/dom';
import { cart, getProduct } from '@/glow';
import { emit } from '@/glow/events';

import ProductForm from '@/lib/product-form';
import {
  quantityInput,
  updatePrices,
  updateSku,
  updateBuyButton,
  optionButtons,
} from '@/lib/product';
import { updateUnitPrices } from '@/lib/unit-pricing';
import dispatchCustomEvent from '@/lib/dispatch-custom-event';

const selectors = {
  form: '[data-product-form]',
  addToCart: '[data-add-to-cart]',
  variantSelect: '[data-variant-select]',
  optionById: id => `[value='${id}']`,
  quantityError: '[data-quantity-error]',
};

export default function QuickAddProductForm(node, openQuickCart) {
  let productForm;
  const formElement = qs(selectors.form, node);
  const quantityError = qs(selectors.quantityError, node);

  const { productHandle } = formElement.dataset;
  const product = getProduct(productHandle);
  product(data => {
    productForm = ProductForm(node, formElement, data, {
      onOptionChange: e => _onOptionChange(e),
      onFormSubmit: e => _onFormSubmit(e),
      onQuantityChange: e => _onQuantityChange(e),
    });
  });

  const optionButtonsEls = optionButtons(qsa('[data-option-buttons]', node));
  const quantityInputEls = quantityInput(node);

  // When the user changes a product option
  function _onOptionChange({ dataset: { variant } }) {
    const buyButton = qs(selectors.addToCart, node);

    // Update prices to reflect selected variant
    updatePrices(node, variant);

    // Update buy button
    updateBuyButton(buyButton, variant);

    // Update unit pricing
    updateUnitPrices(node, variant);

    // Update sku
    updateSku(node, variant);

    dispatchCustomEvent('product:variant-change', { variant: variant });

    if (!variant) {
      updateBuyButton(qs('[data-add-to-cart]', node), false);

      return;
    }

    // We need to set the id input manually so the Dynamic Checkout Button works
    const selectedVariantOpt = qs(
      `${selectors.variantSelect} ${selectors.optionById(variant.id)}`,
      node
    );
    selectedVariantOpt.selected = true;
  }

  function _onFormSubmit(e) {
    const { enableCartAjax } = document.body.dataset;

    // If a featured product section is on the cart page we will need to refresh
    // the cart to show a product has been added.
    const cartPage = document.body.classList.contains('template-cart');

    if (!enableCartAjax || cartPage) return;

    e.preventDefault();

    add(quantityError, 'hidden');

    const button = qs(selectors.addToCart, node);
    add(button, 'loading');
    cart
      .addItem(formElement)
      .then(({ item }) => {
        remove(button, 'loading');
        add(button, 'item-added');
        emit('quick-add:close');
        if (openQuickCart) {
          emit('cart:open', null, { flash: item.variant_id });
        }
        dispatchCustomEvent('cart:item-added', { product: item });
      })
      .catch(() => {
        remove(quantityError, 'hidden');
        const button = qs(selectors.addToCart, node);
        remove(button, 'loading');
      });
  }

  function _onQuantityChange({ dataset: { variant, quantity } }) {
    // Adjust the hidden quantity input within the form
    const quantityInputs = [...qsa('[name="quantity"]', formElement)];
    quantityInputs.forEach(quantityInput => {
      quantityInput.value = quantity;
    });

    dispatchCustomEvent('product:quantity-update', {
      quantity: quantity,
      variant: variant,
    });
  }

  const unload = () => {
    productForm && productForm.destroy();
    optionButtonsEls.destroy();
    quantityInputEls.unload();
  };

  return { unload };
}
